import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  ConfirmationNumberOutlined as TicketIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  SecurityOutlined as SecurityIcon,
  FlashOn as InstantIcon,
  CasinoOutlined as CasinoIcon,
  SportsEsportsOutlined as MineIcon,
  RocketLaunchOutlined as RocketIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";
import {
  DEFAULT_CURRENCY,
  getLotteryTypeName,
} from "../../../../../static/Constants";

const LotterySummaryCard = ({
  lotteryId,
  lotteryType = "2",
  entryFeeFormatted = "0",
  currency = DEFAULT_CURRENCY,
  pickNumbers = 3,
  selectednum = [],
  selectedMissileNumber = "",
  account = "",
  loading = false,
  onBuy,
  onConnectWallet,
}) => {
  const typeId = Number(lotteryType);
  const typeName = getLotteryTypeName(lotteryType);
  const numPick = Number(pickNumbers) || 3;

  // Completion criteria per game type
  const isComplete =
    typeId === 1
      ? selectednum.length === 1
      : typeId === 3
      ? selectedMissileNumber.toString().length > 0
      : typeId === 0
      ? true
      : selectednum.length === numPick;

  const remainingPicks = Math.max(0, numPick - selectednum.length);

  const truncatedAccount = account
    ? `${account.substring(0, 6)}...${account.substring(account.length - 4)}`
    : "";

  return (
    <Card
      className="buylottery-summary-card"
      sx={{
        borderRadius: borderRadiusPx.lg ,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        {/* Ticket Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: borderRadiusPx.default,
                bgcolor:
                  typeId === 1
                    ? colors.purple[50]
                    : typeId === 3
                    ? colors.amber[50]
                    : colors.blue[50],
                color:
                  typeId === 1
                    ? colors.purple[600]
                    : typeId === 3
                    ? colors.amber[600]
                    : colors.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeId === 1 ? (
                <MineIcon fontSize="small" />
              ) : typeId === 3 ? (
                <RocketIcon fontSize="small" />
              ) : (
                <TicketIcon fontSize="small" />
              )}
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[900],
                  lineHeight: 1.2,
                }}
              >
                Order Summary
              </Typography>
              <Typography variant="caption" sx={{ color: colors.slate[500] }}>
                {typeName} #{lotteryId}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Selected Numbers / Target Preview */}
        <Box
          sx={{
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
            borderRadius: borderRadiusPx.md,
            p: 2,
            mb: 2.5,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.25 }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: colors.slate[600],
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {typeId === 1
                ? "Selected Ticket"
                : typeId === 3
                ? "Target Trajectory"
                : "Chosen Numbers"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: isComplete ? colors.emerald[600] : colors.slate[400],
              }}
            >
              {typeId === 1
                ? selectednum.length === 1
                  ? "Ticket Selected"
                  : "0 / 1 selected"
                : typeId === 3
                ? selectedMissileNumber
                  ? "Target Configured"
                  : "Enter Target"
                : `${selectednum.length} / ${numPick} picked`}
            </Typography>
          </Stack>

          {/* Type 1: MineBET Single Ticket */}
          {typeId === 1 ? (
            selectednum.length > 0 ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: borderRadiusPx.circle,
                    background:
                      "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
                    color: "#ffffff !important",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 3px 8px rgba(124, 58, 237, 0.3)",
                  }}
                >
                  {selectednum[0]}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colors.slate[800] }}
                >
                  Ticket #{selectednum[0]}
                </Typography>
              </Stack>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[400],
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                  py: 0.25,
                }}
              >
                No ticket selected yet. Click an available ticket number.
              </Typography>
            )
          ) : typeId === 3 ? (
            /* Type 3: RocketBET Target */
            selectedMissileNumber ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: borderRadiusPx.md,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff !important",
                    fontWeight: 800,
                    fontSize: "1.1rem",
                    fontFamily: "monospace",
                    letterSpacing: 2,
                    boxShadow: "0 3px 8px rgba(245, 158, 11, 0.3)",
                  }}
                >
                  {selectedMissileNumber}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colors.slate[800] }}
                >
                  Launch Target
                </Typography>
              </Stack>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: colors.slate[400],
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                  py: 0.25,
                }}
              >
                Enter target digits on keypad or use Random Target.
              </Typography>
            )
          ) : /* Type 2 / 0: MegaBET & Default */
          selectednum.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
              {selectednum.map((num) => (
                <Box
                  key={num}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: borderRadiusPx.circle,
                    background:
                      "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    color: "#ffffff !important",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 3px 8px rgba(37, 99, 235, 0.28)",
                    userSelect: "none",
                  }}
                >
                  {num}
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: colors.slate[400],
                fontStyle: "italic",
                fontSize: "0.85rem",
                py: 0.25,
              }}
            >
              No numbers selected yet
            </Typography>
          )}
        </Box>

        {/* Cost Breakdown */}
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" sx={{ color: colors.slate[600] }}>
              Ticket Price
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: colors.slate[900] }}
            >
              {entryFeeFormatted} {currency}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" sx={{ color: colors.slate[600] }}>
              Entry Status
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isComplete ? colors.emerald[600] : colors.amber[600],
              }}
            >
              {isComplete
                ? "Ready to Submit"
                : typeId === 1
                ? "Select 1 Ticket"
                : typeId === 3
                ? "Enter Target Number"
                : `Need ${remainingPicks} more`}
            </Typography>
          </Stack>

          <div className="ticket-perforated-divider" />

          {/* Total */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: colors.slate[900] }}
            >
              Total Payable
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: colors.primary.main }}
            >
              {entryFeeFormatted} {currency}
            </Typography>
          </Stack>
        </Stack>

        {/* Connected Wallet Info */}
        <Box sx={{ mb: 2.5 }}>
          {account ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                bgcolor: colors.slate[50],
                border: `1px solid ${colors.slate[200]}`,
                borderRadius: borderRadiusPx.sm,
                p: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: colors.emerald[500],
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: colors.slate[600], fontWeight: 500 }}
              >
                Wallet: <strong>{truncatedAccount}</strong>
              </Typography>
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                bgcolor: colors.amber[50],
                border: `1px solid ${colors.amber[200]}`,
                borderRadius: borderRadiusPx.sm,
                p: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: colors.amber[800], fontWeight: 600 }}
              >
                Wallet Not Connected
              </Typography>
              <CommonButton
                variant="text"
                size="small"
                onClick={onConnectWallet}
                label="Connect"
                sx={{
                  p: 0,
                  minWidth: "auto",
                  fontSize: "0.75rem",
                  color: colors.primary.main,
                  fontWeight: 700,
                }}
              />
            </Stack>
          )}
        </Box>

        {/* Action Button */}
        {!account ? (
          <CommonButton
            fullWidth
            variant="primary"
            size="large"
            startIcon={<WalletIcon />}
            onClick={onConnectWallet}
            label="Connect Wallet to Buy"
          />
        ) : (
          <CommonButton
            fullWidth
            variant="primary"
            size="large"
            loading={loading}
            disabled={!isComplete || loading}
            onClick={onBuy}
            startIcon={
              !loading &&
              (typeId === 1 ? (
                <MineIcon />
              ) : typeId === 3 ? (
                <RocketIcon />
              ) : (
                <CasinoIcon />
              ))
            }
            label={
              loading
                ? "Confirming On-Chain..."
                : isComplete
                ? typeId === 1
                  ? `Buy Ticket #${selectednum[0]} (${entryFeeFormatted} ${currency})`
                  : typeId === 3
                  ? `Launch Rocket (${entryFeeFormatted} ${currency})`
                  : `Buy Ticket (${entryFeeFormatted} ${currency})`
                : typeId === 1
                ? "Select 1 Ticket to Buy"
                : typeId === 3
                ? "Enter Target Number"
                : `Pick ${remainingPicks} More Number(s)`
            }
          />
        )}

        {/* Security / Trust Highlights */}
        <Stack
          spacing={1}
          sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.slate[100]}` }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SecurityIcon sx={{ fontSize: 16, color: colors.emerald[600] }} />
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Provably Fair & On-Chain Randomness
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InstantIcon sx={{ fontSize: 16, color: colors.amber[600] }} />
            <Typography variant="caption" sx={{ color: colors.slate[500] }}>
              Instant Automated Smart Contract Payouts
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LotterySummaryCard;
